import type { Node } from '@xyflow/react';
import type { BaseNodeData } from '../types';

/**
 * Applies publish-validation results to canvas nodes by setting `data.isValid`
 * on every node whose `stepId` appears (or no longer appears) in the invalid set.
 *
 * Identity contract: returns the SAME array instance when no node changed.
 * The canvas syncs its nodes state back to the parent editor on every identity
 * change, and the parent re-validates on every nodes change — so returning a
 * fresh array here when nothing changed closes an infinite render loop.
 */
export function applyStepValidityToNodes(
	nodes: Node<BaseNodeData>[],
	invalidStepIds: Iterable<string>
): Node<BaseNodeData>[] {
	const invalidSet = new Set(invalidStepIds);
	let didChange = false;
	const nextNodes = nodes.map(node => {
		const nodeData = node.data;
		if ('stepId' in nodeData && typeof nodeData.stepId === 'string') {
			const shouldBeValid = !invalidSet.has(nodeData.stepId);
			if (nodeData.isValid === shouldBeValid) {
				return node;
			}
			didChange = true;
			return { ...node, data: { ...nodeData, isValid: shouldBeValid } };
		}
		return node;
	});
	return didChange ? nextNodes : nodes;
}
